import { useCallback, useEffect, useRef } from "react";
import { useData } from "@hooks/useData";

/** A promise that never settles: `useData` then never applies the superseded response. */
const never = <T,>() => new Promise<T>(() => {});

/**
 * `useData` for requests keyed by one string (e.g. a food word), adding what `useData` lacks:
 * - starting a new request aborts the previous one, and unmounting aborts the last;
 * - a superseded request never settles, so it can't overwrite the newer request's data or loading state
 *   (`useData` has no stale-response guard);
 * - an empty key sends no request and resolves to `emptyData`;
 * - a failed request (204 empty body, 400, unparseable response) resolves to `emptyData` and is logged.
 *
 * `request` must be stable (module-level), as with `useData`.
 */
export default function useAbortableData<T>(
    emptyData: T,
    request: (key: string, signal: AbortSignal) => Promise<T>,
    key: string,
    label: string,
) {
    const abortRef = useRef<AbortController | null>(null);

    const fetchFn = useCallback((requestKey: string): Promise<T> => {
        abortRef.current?.abort();
        if (!requestKey) return Promise.resolve(emptyData);

        const controller = new AbortController();
        abortRef.current = controller;
        const { signal } = controller;

        return request(requestKey, signal).then(
            data => (signal.aborted ? never<T>() : data),
            error => {
                if (signal.aborted) return never<T>();
                console.error("Fetch error:", label, error);
                return emptyData;
            },
        );
    }, [emptyData, request, label]);

    useEffect(() => () => abortRef.current?.abort(), []);

    return useData<T>(emptyData, fetchFn, key);
}
